// Dati economici della proforma da contabilità (mandato e servizio) inviati al BE,
// così il record salvato ha gli stessi totali di anteprima e PDF.
import { ProformaRow } from './generaProformaPdf';
import { IvaMode, splitRettifica, round2, ALIQUOTA_IVA } from './contabilitaCanali';

export interface ProformaEconomia {
    imponibile: number;        // somma righe inTotal (come prima)
    imponibileEsente?: number; // righe inTotal && noIva: fuori dalla base IVA
    ivaExtra?: number;         // IVA non proporzionale (rettifiche "Solo IVA", arrotondamenti dei canali)
}

type RigaTotale = Pick<ProformaRow, 'importo' | 'inTotal' | 'noIva'>;

/**
 * Campi economici del payload di create. Esente ed extra si mandano solo se diversi
 * da zero: le proforma senza righe esenti né IVA extra producono lo stesso payload di prima.
 */
export const economiaProforma = (rows: RigaTotale[], ivaExtra = 0): ProformaEconomia => {
    const imponibile = rows.reduce((s, r) => s + (r.inTotal !== false ? Number(r.importo) || 0 : 0), 0);
    const esente = round2(rows.reduce((s, r) => s + (r.inTotal !== false && r.noIva ? Number(r.importo) || 0 : 0), 0));
    const extra = round2(Number(ivaExtra) || 0);
    return {
        imponibile,
        ...(esente !== 0 ? { imponibileEsente: esente } : {}),
        ...(extra !== 0 ? { ivaExtra: extra } : {}),
    };
};

/**
 * Marcatore per l'hash di dedup: presente solo quando il record ha esente o IVA extra, così
 * non si riusa una proforma identica salvata col vecchio calcolo (IVA su tutto l'imponibile).
 * Per le altre proforma l'hash non cambia.
 */
export const hashMarkerBaseIva = (e: ProformaEconomia): { b?: number } =>
    (e.imponibileEsente !== undefined || e.ivaExtra !== undefined) ? { b: 2 } : {};

/**
 * Riga proforma di una rettifica secondo la modalità IVA, come nella proforma del mandato:
 * IVA inclusa → solo la parte imponibile; Solo IVA → riga informativa fuori dal totale
 * (la sua IVA va in ivaExtra); Solo imponibile → nel totale ma fuori dalla base IVA.
 */
export const rigaRettificaProforma = (importo: number, mode: IvaMode): { importo: number; inTotal: boolean; noIva?: boolean } => {
    const s = splitRettifica(Number(importo) || 0, mode);
    if (mode === 'SOLO_IVA') return { importo: s.iva, inTotal: false };
    if (mode === 'LORDO') return { importo: s.imp, inTotal: true };
    // NETTO e Solo imponibile: importo così com'è.
    return { importo: Number(importo) || 0, inTotal: true, ...(mode === 'SOLO_IMPONIBILE' ? { noIva: true } : {}) };
};

/**
 * IVA extra della proforma allineata ai canali (stessa formula della proforma del mandato):
 * IVA dovuta meno il 22% della quota soggetta delle righe.
 */
export const ivaExtraDaCanali = (ivaDovuta: number, imponibileDovuto: number, esclusiRighe: number): number =>
    Math.round((ivaDovuta - (imponibileDovuto - esclusiRighe) * (ALIQUOTA_IVA / 100)) * 100) / 100;
